import styled, { css } from 'styled-components';
import {
  Button,
  CloseButton,
  Flex,
  Icon,
  Link,
  styleMixinFocusOutline,
  Text,
  TextButton,
} from '@amzn/storm-ui';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';

const focusNavElementMixin = css`
  &&&:focus,
  &&&:focus-visible {
    ${styleMixinFocusOutline};
  }
`;

const navLinkStyleMixin = css`
  &&&& {
    color: ${({ theme }) => theme.nav.link.color};
    border-radius: ${({ theme }) => theme.border.radius.small};
    padding: ${({ theme }) => theme.spacing.xsmall.default};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.xsmall.default};
    text-decoration: none;
    width: 100%;

    :hover {
      color: ${({ theme }) => theme.text.color.secondary};
      background-color: ${({ theme }) =>
        theme.selectableContainer.backgroundColor.hover};
    }

    :active {
      color: ${({ theme }) => theme.text.color.tertiary};
      background-color: ${({ theme }) =>
        theme.selectableContainer.backgroundColor.selected};
    }

    ${focusNavElementMixin};
  }
`;

export const MobileNavWrapper = styled('div')`
  background-color: ${({ theme }) => theme.surface.container.default};
  box-sizing: border-box;
  display: block;
  min-height: 100vh;
  height: 100%;
  overflow-y: auto;
  padding: 0
    ${({ theme }) =>
      `${theme.spacing.xsmall.default} ${theme.spacing.xsmall.default}`};
`;

export const SecondaryMenuWrapper = styled(Flex).attrs({
  direction: 'column',
  padding: 'xxsmall',
  alignItems: 'stretch',
})``;

export const MobileMenuCloseBtn = styled(CloseButton)`
  user-select: none;
  margin-inline-end: ${({ theme }) => theme.spacing.xsmall.default};
  width: 40px;
  height: 40px;
  padding: 0;
  ${focusNavElementMixin};
`;

export const MobileLogoWrapper = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing.xxxsmall.default};
  border-radius: ${({ theme }) => theme.border.radius.small};
  ${focusNavElementMixin};
`;

export const MobileLogo = styled.img`
  width: 100px;
  vertical-align: bottom;
`;

export interface NonExpandableItemProps {
  isSelected: boolean | undefined;
}

export const NonExpandableItemLink = styled(Link)<NonExpandableItemProps>`
  ${navLinkStyleMixin};

  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${({ theme }) =>
        theme.selectableContainer.backgroundColor.selected};
      font-weight: bold;
    `};
`;

export const MobileSideNavItemText = styled(Flex).attrs({
  justifyContent: 'space-between',
  alignItems: 'center',
})`
  width: 100%;
  font-family: 'Amazon Ember', Arial, Helvetica, sans-serif;
  font-size: 15px;
  text-align: start;
`;

export interface ExpandableListProps {
  isExpanded: boolean | undefined;
}

export const ExpandableItemWrapper = styled('div')<ExpandableListProps>`
  ${({ isExpanded, theme }) =>
    isExpanded &&
    css`
      margin-block-end: ${theme.spacing.xsmall.mobile};
    `};
`;

export const ExpandableItemBtn = styled(TextButton)`
  ${navLinkStyleMixin};
`;

export const MobileDropdownIcon = styled(Icon)`
  font-size: 15px;
  margin-inline-start: auto;
`;

export const ExpandedListWrapper = styled('div')`
  font-size: 15px;
  margin-inline-start: 22px;
  padding-inline-start: ${({ theme }) => theme.spacing.xxsmall.mobile};
  border-inline-start: 2px solid
    ${({ theme }) => theme.border.color.subtle.default};
`;

export interface ExpandedListItemProps {
  isSelected: boolean | undefined;
}

export const ExpandedListItem = styled(Link)<ExpandedListItemProps>`
  ${navLinkStyleMixin};

  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${({ theme }) =>
        theme.selectableContainer.backgroundColor.selected};
      font-weight: bold;
      color: ${({ theme }) => theme.color.gray[0]};
    `};
`;

export const MobileLanguageSwitcher = styled.div`
  color: ${({ theme }) => theme.nav.link.color};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  width: 100%;
`;

export const MobileLanguageSwitchBtn = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing.xsmall.default};
  width: 100%;
  line-height: 20px;
  font-size: 15px;
  padding: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const MobileNavSheetInnerContainer = styled.div<{
  hasInnerPadding: boolean;
}>`
  height: 100%;
  width: 77vw;
  ${({ hasInnerPadding, theme }) =>
    hasInnerPadding &&
    css`
      padding: ${theme.spacing.xsmall.default};
    `}
`;

export const ExpandedListGroupTitle = styled(Text).attrs({
  weight: 'bold',
})`
  font-size: 12px;
  line-height: 18px;
  text-transform: uppercase;
  padding: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const StyledSvgIcon = styled(SvgIcon)`
  svg {
    width: 24px;
    height: 24px;
  }
`;
